/**
 * Metering Saga
 *
 * Redux Saga middleware for metering async operations.
 * Handles API calls, error management, and state updates.
 *
 * Data Flow:
 * 1. Component dispatches PENDING action (e.g., getMeteringOverviewContractedPending())
 * 2. watchGetMeteringOverviewContracted saga listener catches the action
 * 3. Worker saga (getMeteringOverviewContracted) calls service function
 * 4. Service fetches data and extracts relevant endpoint
 * 5. On success: dispatch FULFILLED action with data
 * 6. On error: dispatch REJECTED action with error object
 * 7. Reducer updates Redux state
 * 8. Component receives updated state via mapStateToProps
 *
 * For each endpoint, there are:
 * - Worker saga: Actually performs the work (call service, dispatch action)
 * - Watcher saga: Listens for PENDING action and spawns worker
 */

import { call, put, takeEvery } from "redux-saga/effects";
import * as meteringService from "../../services/meteringService";
import getResourceError from "./helpers/getResourceError";
import {
  addMeteringAssetsFulfilled,
  addMeteringAssetsRejected,
  CREATE_ADD_METERING_ASSETS_PENDING,
  GET_METERING_CONTRACTS_BY_CUSTOMER_PENDING,
  GET_METERING_OVERVIEW_CONTRACTED_PENDING,
  GET_METERING_TABLE_DETAILS_PENDING,
  GET_METERING_TABLE_CONTRACTS_PENDING,
  GET_METERING_CAPACITY_CSV_DOWNLOAD_PENDING,
  GET_METERING_CONTRACTS_CSV_DOWNLOAD_PENDING,
  getMeteringContractsByCustomerFulfilled,
  getMeteringContractsByCustomerRejected,
  getMeteringOverviewContractedFulfilled,
  getMeteringOverviewContractedRejected,
  getMeteringTableDetailsFulfilled,
  getMeteringTableDetailsRejected,
  getMeteringTableContractsFulfilled,
  getMeteringTableContractsRejected,
  getMeteringCapacityCSVDownloadFulfilled,
  getMeteringCapacityCSVDownloadRejected,
  getMeteringContractsCSVDownloadFulfilled,
  getMeteringContractsCSVDownloadRejected
} from "../actions/meteringActions";
import { handleLoader } from "../actions/loaderAction";
import { CONTRACT_FIELD_KEYS } from "../../containers/metering/constants/meteringConstants";
import { notificationSend } from "../actions/notificationActions";
import {
  formatDateToLocale,
  isNativeIOSApp
} from "../../utils/clearsightUtils";

function* showNotification(message, responseStatus) {
  yield put(
    notificationSend({
      id: new Date(),
      label: message,
      variant: responseStatus,
      dismissAfter: 5000
    })
  );
}

/**
 * OVERVIEW SAGAS
 * Worker and watcher sagas for contracted/used capacity endpoints
 */

// Worker: Called when GET_METERING_OVERVIEW_CONTRACTED_PENDING is dispatched
//Capacity info
export function* getMeteringOverviewContracted(action) {
  try {
    const response = yield call(
      meteringService.getMeteringOverviewContracted,
      action?.payload
    );
    yield put(getMeteringOverviewContractedFulfilled(response));
  } catch (error) {
    const resourceError = yield call(
      getResourceError,
      error,
      "GET",
      "METERING_OVERVIEW_CONTRACTED",
      "Oops. An error occurred when fetching the contracted capacity details."
    );
    yield call(
      showNotification,
      error?.error || resourceError?.message,
      "error"
    );
    yield put(getMeteringOverviewContractedFulfilled(null));
    yield put(getMeteringOverviewContractedRejected(resourceError));
  }
}

//capacity details table
export function* getMeteringTableDetails(action) {
  try {
    const response = yield call(
      meteringService.getMeteringTableDetails,
      action?.payload
    );
    yield put(getMeteringTableDetailsFulfilled(response));
  } catch (error) {
    const resourceError = yield call(
      getResourceError,
      error,
      "GET",
      "METERING_TABLE_DETAILS",
      "Oops. An error occurred when fetching details table data."
    );
    yield call(
      showNotification,
      error?.error || resourceError?.message,
      "error"
    );
    yield put(getMeteringTableDetailsFulfilled(null));
    yield put(getMeteringTableDetailsRejected(resourceError));
  }
}

export function* getMeteringContractsTable(action) {
  try {
    const response = yield call(
      meteringService.getMeteringContractsTable,
      action?.payload
    );
    yield put(getMeteringTableContractsFulfilled(response));
  } catch (error) {
    const resourceError = yield call(
      getResourceError,
      error,
      "GET",
      "METERING_TABLE_CONTRACTS",
      "Oops. An error occurred when fetching contracts table data."
    );
    yield call(
      showNotification,
      error?.error || resourceError?.message,
      "error"
    );
    yield put(getMeteringTableContractsRejected(resourceError));
  }
}

export function* getMeteringContractsByCustomer() {
  try {
    const response = yield call(meteringService.getCustomerContracts);
    yield put(getMeteringContractsByCustomerFulfilled(response));
  } catch (error) {
    const resourceError = yield call(
      getResourceError,
      error,
      "GET",
      "METERING_CONTRACTS_BY_CUSTOMER",
      "Oops. An error occurred when fetching the list of customer contracts."
    );
    yield call(
      showNotification,
      error?.error || resourceError?.message,
      "error"
    );
    yield put(getMeteringContractsByCustomerRejected(resourceError));
  }
}

export function* addMeteringAssets(action) {
  try {
    yield put(handleLoader(true));
    const response = yield call(
      meteringService.addMeteringAssets,
      action.payload
    );

    yield put(addMeteringAssetsFulfilled(response));
    yield call(showNotification, response?.message, "success");
    yield put(handleLoader(false));
  } catch (error) {
    const resourceError = yield call(
      getResourceError,
      error,
      "ADD",
      "ASSETS",
      "Oops. An error occurred when adding the asset."
    );
    yield call(
      showNotification,
      error?.message || resourceError?.message,
      "error"
    );
    yield put(
      addMeteringAssetsRejected({ error: resourceError, value: error })
    );
    yield put(handleLoader(false));
  }
}

// Processes CSV content and formats target date columns
const processCSVForExcel = (csvText, targetColumns = []) => {
  if (!csvText || !targetColumns.length) return csvText;

  const rows = csvText.split(/\r?\n/);

  if (rows.length < 2) {
    return csvText;
  }

  /**
   * REGEX EXPLAINED: /,(?=(?:(?:[^"]*"){2})*[^"]*$)/
   * This is a 'Positive Lookahead'. It splits by a comma ONLY if that
   * comma is followed by an even number of quotes. This ensures we don't
   * split on a comma that sits inside a quoted string like "City, State".
   */
  const CSV_COMMA_SPLIT_REGEX = /,(?=(?:(?:[^"]*"){2})*[^"]*$)/;
  const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

  // Normalize helper
  const normalize = value =>
    value
      .replace(/^"|"$/g, "")
      .trim()
      .toLowerCase();

  // Convert target columns into Set for faster lookup
  const targetSet = new Set(targetColumns.map(normalize));

  // Find matching column indexes once
  const headers = rows[0].split(CSV_COMMA_SPLIT_REGEX).map(normalize);

  //find the target columns index
  const targetIndexes = headers.reduce((acc, header, index) => {
    if (targetSet.has(header)) {
      acc.push(index);
    }

    return acc;
  }, []);

  // Process rows
  const processedRows = rows.map((row, rowIndex) => {
    // Keep header unchanged
    if (rowIndex === 0) {
      return row;
    }

    //skip empty rows
    if (!row.trim()) {
      return "";
    }

    // Skip splitting if no target columns exist
    if (!targetIndexes.length) {
      return row;
    }

    const columns = row.split(CSV_COMMA_SPLIT_REGEX);

    // Format matching date columns for Excel
    for (const index of targetIndexes) {
      const value = columns[index]?.trim();

      if (DATE_REGEX.test(value)) {
        columns[index] = `="${value}"`;
      }
    }
    return columns.join(",");
  });

  return processedRows.join("\n");
};

// Downloads processed CSV file — uses native iOS bridge when running in the React Native shell
const downloadCSVFile = (csvText, fileName, targetColumns = []) => {
  const formattedCSV = processCSVForExcel(csvText, targetColumns);

  if (isNativeIOSApp()) {
    const base64 = btoa(unescape(encodeURIComponent(formattedCSV)));
    const csvFileName = fileName.endsWith(".csv")
      ? fileName
      : `${fileName}.csv`;
    if (
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.ReactNative
    ) {
      window.webkit.messageHandlers.ReactNative.postMessage(
        JSON.stringify({
          type: "SAVE_CSV",
          data: base64,
          filename: csvFileName
        })
      );
    }
    return formattedCSV;
  }

  // Add BOM for proper Excel UTF-8 support
  const blob = new Blob(["\uFEFF", formattedCSV], {
    type: "text/csv;charset=utf-8;"
  });

  const url = URL.createObjectURL(blob);

  // Create temporary download link
  const link = Object.assign(document.createElement("a"), {
    href: url,
    download: fileName.endsWith(".csv") ? fileName : `${fileName}.csv`
  });

  link.click();

  // Cleanup memory
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 100);

  return formattedCSV;
};
//capacity csv download
export function* getCapacityReportCSV({ payload }) {
  try {
    const response = yield call(meteringService.getCapacityReportCSV, payload);

    const statusCode = response.status;

    let csvText = yield call([response, "text"]);

    const fileName = `Capacity Details ${
      payload?.serial_number
    } - ${formatDateToLocale(payload?.from_date)}-${formatDateToLocale(
      payload?.to_date
    )}.csv`;
    csvText = downloadCSVFile(csvText, fileName, [CONTRACT_FIELD_KEYS.DATE]);

    yield put(
      getMeteringCapacityCSVDownloadFulfilled({
        data: csvText,
        status: statusCode
      })
    );
  } catch (error) {
    const resourceError = yield call(
      getResourceError,
      error,
      "GET",
      "METERING_CAPACITY_REPORT",
      error?.error || "Oops. An error occurred while downloading the report."
    );
    yield call(showNotification, resourceError?.message, "error");
    yield put(getMeteringCapacityCSVDownloadRejected(resourceError));
  }
}

//contracts csv download
export function* getContractsReportCSV(action) {
  try {
    const response = yield call(
      meteringService.getContractsReportCSV,
      action.payload
    );

    const statusCode = response.status;

    let csvText = yield call([response, "text"]);

    csvText = downloadCSVFile(csvText, "Contracts Details.csv");

    yield put(
      getMeteringContractsCSVDownloadFulfilled({
        data: csvText,
        status: statusCode
      })
    );
  } catch (error) {
    const resourceError = yield call(
      getResourceError,
      error,
      "GET",
      "METERING_CONTRACTS_REPORT",
      error?.error || "Oops. An error occurred while downloading the report."
    );
    yield call(showNotification, resourceError?.message, "error");
    yield put(getMeteringContractsCSVDownloadRejected(resourceError));
  }
}

/**
 * WATCHER SAGAS
 * Each watches for a specific action and triggers the worker saga
 * Format: takeEvery(ACTION_TYPE, workerFunction)
 * takeEvery allows multiple concurrent requests
 */

function* watchGetMeteringOverviewContracted() {
  yield takeEvery(
    GET_METERING_OVERVIEW_CONTRACTED_PENDING,
    getMeteringOverviewContracted
  );
}

function* watchGetMeteringTableDetails() {
  yield takeEvery(GET_METERING_TABLE_DETAILS_PENDING, getMeteringTableDetails);
}

function* watchGetMeteringTableContracts() {
  yield takeEvery(
    GET_METERING_TABLE_CONTRACTS_PENDING,
    getMeteringContractsTable
  );
}

function* watchGetMeteringContractsByCustomer() {
  yield takeEvery(
    GET_METERING_CONTRACTS_BY_CUSTOMER_PENDING,
    getMeteringContractsByCustomer
  );
}

function* watchAddMeteringAssets() {
  yield takeEvery(CREATE_ADD_METERING_ASSETS_PENDING, addMeteringAssets);
}

function* watchGetCapacityReportCSV() {
  yield takeEvery(
    GET_METERING_CAPACITY_CSV_DOWNLOAD_PENDING,
    getCapacityReportCSV
  );
}

function* watchGetContractsReportCSV() {
  yield takeEvery(
    GET_METERING_CONTRACTS_CSV_DOWNLOAD_PENDING,
    getContractsReportCSV
  );
}

/**
 * Export all watcher sagas
 * These are registered in rootSaga.js and run on app startup
 * Each watcher listens for its corresponding PENDING action
 */
export default [
  watchGetMeteringOverviewContracted,
  watchGetMeteringTableDetails,
  watchGetMeteringTableContracts,
  watchGetMeteringContractsByCustomer,
  watchAddMeteringAssets,
  watchGetCapacityReportCSV,
  watchGetContractsReportCSV
];
